import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, FileText, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PREVIEWABLE = ['.pdf', '.jpg', '.jpeg', '.png', '.webp', '.gif', '.txt', '.csv'];

export const isPreviewable = (filename = '') => PREVIEWABLE.some((ext) => filename.toLowerCase().endsWith(ext));

export const StagingAttachmentPreview = ({ emailId, attachment, open, onOpenChange }) => {
  const token = localStorage.getItem('access_token');
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !attachment) { setUrl(null); return undefined; }
    let objectUrl;
    setLoading(true);
    axios.get(`${API_URL}/staging-inbox/${emailId}/attachment/${attachment.index}?inline=true`, {
      headers: { Authorization: `Bearer ${token}` }, responseType: 'blob',
    }).then(({ data }) => {
      objectUrl = URL.createObjectURL(data);
      setUrl(objectUrl);
    }).catch((error) => {
      toast.error(error.response?.status === 410
        ? 'This attachment was not retained — use "Re-fetch attachments"'
        : 'Could not open this attachment');
      onOpenChange(false);
    }).finally(() => setLoading(false));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [open, attachment, emailId, token, onOpenChange]);

  if (!attachment) return null;
  const name = (attachment.filename || '').toLowerCase();
  const isImage = ['.jpg', '.jpeg', '.png', '.webp', '.gif'].some((ext) => name.endsWith(ext));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="staging-attachment-preview-dialog">
        <DialogHeader>
          <DialogTitle className="truncate">{attachment.filename}</DialogTitle>
          <DialogDescription>{Math.round((attachment.size || 0) / 1024)} KB · {attachment.mime_type}</DialogDescription>
        </DialogHeader>

        <div className="h-[62vh] border bg-slate-50" data-testid="staging-attachment-preview-body">
          {loading && <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>}
          {!loading && url && (isImage
            ? <img src={url} alt={attachment.filename} className="mx-auto h-full object-contain" data-testid="staging-attachment-preview-image" />
            : <iframe src={url} title={attachment.filename} className="h-full w-full" data-testid="staging-attachment-preview-frame" />)}
          {!loading && !url && (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
              <FileText className="h-8 w-8" /> No inline preview for this file type — download it instead.
            </div>
          )}
        </div>

        <DialogFooter>
          {url && (
            <Button variant="outline" asChild data-testid="staging-attachment-preview-download">
              <a href={url} download={attachment.filename}><Download className="mr-2 h-4 w-4" /> Download</a>
            </Button>
          )}
          <Button onClick={() => onOpenChange(false)} data-testid="staging-attachment-preview-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
